import { UcoastEl } from '@/scripts/core/UcoastEl'
import EmblaCarousel, {
    EmblaCarouselType,
    EmblaOptionsType,
} from 'embla-carousel'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import ClassNames from 'embla-carousel-class-names'
import AutoHeight from 'embla-carousel-auto-height'
import Autoplay, { AutoplayType } from "embla-carousel-autoplay"
import { ScrollProgress } from 'embla-carousel/components/ScrollProgress'

export class EmblaSlider extends UcoastEl {
    static htmlSelector = 'embla-slider'
    emblaApi?: EmblaCarouselType
    private autoplayInstance?: AutoplayType
    private dots: HTMLButtonElement[] = [] // Added type annotation

    constructor() {
        super()
        void this.initializeSlider()
    }

    async initializeSlider() {
        const OPTIONS: EmblaOptionsType = {
            slidesToScroll: 'auto',
            align: 'center',
            containScroll: 'keepSnaps',
            dragFree: false,
            duration: this.dataset.duration ? Number(this.dataset.duration) : 1,
            dragThreshold: this.dataset.dragThreshold ? parseInt(this.dataset.dragThreshold) : 0.1,
            loop: this.dataset.loop ? this.dataset.loop === 'true' : false,
            watchDrag: this.dataset.watchDrag ? this.dataset.watchDrag === 'true' : true
        }

        const viewportNode = q.rs('.embla__viewport', this)
        try {
            const plugins = [
                WheelGesturesPlugin(),
                ClassNames(),
                AutoHeight(),
                ...(this.dataset.autoPlay === 'true' ? [Autoplay({delay: this.dataset.delay ? parseInt(this.dataset.delay) : 0, stopOnInteraction: false})] : [])
            ]

            this.emblaApi = EmblaCarousel(viewportNode, OPTIONS, plugins)

            if(this.dataset.autoPlay === 'true') {
                this.autoplayInstance = plugins.find((plugin) => typeof plugin === 'object' && plugin && 'reset' in plugin) as AutoplayType | undefined
            }

            if (this.emblaApi) {

                if(this.querySelector('.embla__dots')) {
                    const emblaDots = q.rs('.embla__dots', this)
                    this.setupDots(emblaDots)
                }

                if(this.querySelector('.embla__scrollbar')) {
                    const scrollbarNode = q.rs('.embla__scrollbar', this)
                    this.setupScrollbar(scrollbarNode)
                }

                this.querySelector('.embla__prev')?.addEventListener('click', this.scrollPrev.bind(this))
                this.querySelector('.embla__next')?.addEventListener('click', this.scrollNext.bind(this))

                if(!this.hasAttribute('data-scroll-flexible')) this.emblaApi.on('scroll', this.onScroll.bind(this))
            }
        } catch (e) {
            console.error(e)
        }
    }

    private setupDots(emblaDots: HTMLElement) {
        const slides = this.emblaApi?.slideNodes() || []
        const slideShow = this.dataset.slideShow || 1
        if(Number(slideShow) >= slides.length) return
        const createDots = () => {
            emblaDots.innerHTML = ''
            this.dots = []
            
            this.dots = Array.from({ length: slides.length }).map((_, index) => {
                const dot = document.createElement('button')
                dot.className = 'embla__dot'
                dot.style.width = `${100 / slides.length}%`
                dot.innerHTML = `<span class="visually-hidden">${index}</span>`
                dot.addEventListener('click', () => this.emblaApi?.scrollTo(index))
                emblaDots.appendChild(dot)
                return dot
            })
        }

        const updateDots = () => {
            const selectedIndex = this.emblaApi?.selectedScrollSnap() || 0
            this.dots.forEach((dot, index) => {
                dot.classList.toggle('embla__dot--selected', index === selectedIndex)
            })
        }

        createDots()
        updateDots()

        this.emblaApi?.on('select', updateDots)
    }

    private setupScrollbar(scrollbarNode: HTMLElement) {
        const enableScrollbar = this.dataset.scrollbar ? this.dataset.scrollbar == 'true' : false
        if(enableScrollbar) {
            const scrollbarThumb = q.rs('.embla__scrollbar__thumb', scrollbarNode)
            const slides = this.emblaApi?.slideNodes() || []
            const slideShow = this.dataset.mobileShow || 1
            if(slides.length > Number(slideShow)) {
                this.emblaApi?.on('scroll', () => {
                    const range = scrollbarNode.offsetWidth - scrollbarThumb.offsetWidth
                    const scorllProgress = Number(this.emblaApi?.scrollProgress())
                    const thumbPosition = scorllProgress * range
                    scrollbarThumb.style.transform = `translateX(${thumbPosition}%)`
                })
            } else {
                scrollbarNode.remove()
            }
        } else {
            scrollbarNode.remove()
        }
    }

    onScroll() {
        if(!this.emblaApi) return
        const scrollProgress = this.emblaApi.scrollProgress()
        if (scrollProgress >= 1 || scrollProgress <= 0) {
            this.emblaApi.internalEngine().translate.toggleActive(false)
        } else {
            this.emblaApi.internalEngine().translate.toggleActive(true)
        }
    }

    scrollPrev() {
        this.emblaApi?.scrollPrev()
        if(this.dataset.autoPlay === 'true') this.autoplayInstance?.reset()
    }

    scrollNext() {
        this.emblaApi?.scrollNext()
        if(this.dataset.autoPlay === 'true') this.autoplayInstance?.reset()
    }

    getSelectedIndex() {
        return this.emblaApi?.selectedScrollSnap() || 0
    }

    scrollMove(index: number) {
        setTimeout(() => {
            this.emblaApi?.scrollTo(index)
        }, 100)
    }
}